import { ArrowRightThin } from '@ori-ui/icons';
import { type ButtonProps, Stack, styled } from '@ori-ui/mui';
import useTestId from '@ori/testid-generator';
import { useMemo } from 'react';
import type { FC } from 'react';

import type { ClickedItem, OnMenuClickCallback } from '../../models';
import type { MenuItem } from '../../schemas/menuItemSchema';
import { isCurrentLocation } from '../../utils/isCurrentLocation';
import { Icon } from './Icon';

interface AccordionMenuItemProps {
  menuItem: MenuItem;
  menuClickedItem: ClickedItem;
  onMenuClick: OnMenuClickCallback;
  isSubmenu?: boolean;
}

const SubList = styled('ul', { shouldForwardProp: (prop) => prop !== 'isOpen' })<{
  isOpen?: boolean;
}>(({ theme, isOpen = false }) => ({
  maxHeight: 0,
  margin: 0,
  padding: 0,
  overflow: 'hidden',
  transitionDuration: '0.2s',
  transitionTimingFunction: 'cubic-bezier(0, 1, 0.5, 1)',
  background: theme.palette.background.paper,
  listStyle: 'none',
  ...(isOpen && {
    maxHeight: theme.spacing(500),
    transitionDuration: '0.4s',
    transitionTimingFunction: 'ease-in',
  }),
}));

const CustomLinkItem = styled('a', {
  shouldForwardProp: (prop) => prop !== 'hasIcon' && prop !== 'isActive',
})<{
  hasIcon?: boolean;
  isActive?: boolean;
}>(({ theme, hasIcon = false, isActive = false }) => ({
  display: 'block',
  position: 'relative',
  boxSizing: 'border-box',
  padding: theme.spacing(2, 3, 2, 0),
  overflow: 'hidden',
  color: theme.palette.grey[700],
  backgroundColor: 'transparent',
  fontSize: '15px',
  letterSpacing: '0.5px',
  textAlign: 'left',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  userSelect: 'none',
  ...(hasIcon
    ? {
        lineHeight: theme.spacing(6),
        fontWeight: 'bold',
        '&:hover': {
          color: theme.palette.grey[700],
          backgroundColor: theme.palette.grey[100],
        },
        ...(isActive && {
          backgroundColor: theme.palette.grey[100],
        }),
      }
    : {
        lineHeight: theme.spacing(4),
        '&:hover': {
          color: theme.palette.grey[400],
        },
        ...(isActive && {
          fontWeight: 'bold',
        }),
      }),
}));

const CustomButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'isOpen' && prop !== 'isClicked' && prop !== 'hasIcon',
})<{
  hasIcon?: boolean;
  isOpen?: boolean;
  isClicked?: boolean;
}>(({ theme, hasIcon = false, isOpen = false, isClicked = false }) => ({
  display: 'flex',
  position: 'relative',
  justifyContent: 'space-between',
  boxSizing: 'border-box',
  padding: theme.spacing(0, 2, 0, 0),
  color: theme.palette.grey[700],
  backgroundColor: 'transparent',
  fontSize: '15px',
  letterSpacing: '0.5px',
  textAlign: 'left',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  userSelect: 'none',
  ...(hasIcon
    ? {
        fontWeight: 'bold',
        // eslint-disable-next-line @typescript-eslint/naming-convention -- this is the syntax
        ':hover': {
          backgroundColor: theme.palette.grey[100],
          cursor: 'pointer',
        },
      }
    : {
        '&:hover': {
          color: theme.palette.grey[400],
        },
      }),
  ...(isOpen &&
    !isClicked && {
      fontWeight: 'bold',
    }),
  ...(isOpen &&
    !hasIcon && {
      color: theme.palette.grey[700],
    }),
}));

const Item = styled('li')(({ theme }) => ({
  borderBottom: `2px solid ${theme.palette.background.paper}`,
  listStyle: 'none',
  padding: 0,
  '& ul': {
    '& li': {
      marginLeft: theme.spacing(3),
    },
  },
}));

const SubItem = styled(Item)({
  borderBottom: 'initial',
});

const Text = styled('span')({
  textOverflow: 'ellipsis',
  overflow: 'hidden',
});

const Caret = styled(ArrowRightThin, {
  shouldForwardProp: (prop) => prop !== 'isOpen' && prop !== 'isSubmenu',
})<{
  isOpen?: boolean;
  isSubmenu?: boolean;
}>(({ isOpen = false, isSubmenu = false }) => ({
  transition: 'transform ease-in 0.3s',
  transform: `rotate(${isOpen ? 270 : 90}deg)`,
  fontSize: isSubmenu ? '1.8rem' : '2.4rem',
}));

export const AccordionMenuItem: FC<AccordionMenuItemProps> = ({
  menuItem,
  menuClickedItem,
  onMenuClick,
  isSubmenu = false,
}) => {
  const { getTestId } = useTestId();
  const isOpen = useMemo((): boolean => {
    if (menuClickedItem.path === menuItem.itemPath) {
      return menuClickedItem.open;
    }

    return menuClickedItem.path.startsWith(menuItem.itemPath);
  }, [menuClickedItem, menuItem]);

  const handleItemClick: ButtonProps['onClick'] = (event) =>
    onMenuClick(event, {
      path: menuItem.itemPath,
      open: !isOpen,
    });

  let itemContent = null;
  let subItems = null;
  const ItemComponent = isSubmenu ? SubItem : Item;

  if (menuItem.subItems.length) {
    itemContent = (
      <CustomButton
        isOpen={menuClickedItem.open}
        isClicked={!isOpen}
        hasIcon={Boolean(menuItem.icon)}
        onClick={handleItemClick}
      >
        <Stack
          direction="row"
          overflow="hidden"
        >
          <Icon icon={menuItem.icon ?? null} />
          <Text>{menuItem.title}</Text>
        </Stack>
        <Caret
          isOpen={isOpen}
          isSubmenu={isSubmenu}
        />
      </CustomButton>
    );

    subItems = (
      <SubList isOpen={isOpen}>
        {menuItem.subItems.map((subitem) => (
          <AccordionMenuItem
            key={subitem.itemPath}
            isSubmenu
            menuItem={subitem}
            menuClickedItem={menuClickedItem}
            onMenuClick={onMenuClick}
          />
        ))}
      </SubList>
    );
  } else {
    itemContent = (
      <CustomLinkItem
        href={menuItem.url ?? ''}
        target={menuItem.openInNewTab ? '_blank' : '_self'}
        hasIcon={Boolean(menuItem.icon)}
        isActive={isCurrentLocation(menuItem.url)}
      >
        <Stack direction="row">
          <Icon icon={menuItem.icon ?? null} />
          <Text>{menuItem.title}</Text>
        </Stack>
      </CustomLinkItem>
    );
  }

  return (
    <ItemComponent data-testid={getTestId(`accordion-${menuItem.itemPath.replaceAll('/', '')}`)}>
      {itemContent}
      {subItems}
    </ItemComponent>
  );
};
